import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import ChevronRight from "@material-ui/icons/ChevronRight";
import React from "react";
import { useHistory } from "react-router";

import { PropertyType } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import { homeCardStyle } from "@/components/home/HomeSection";
import { CashflowRoundingNote } from "@/components/management/CashflowRoundingNote";
import { layoutTokens, legacyColors, semanticColors, textStyles } from "@/designTokens";
import { buildMonthlyCashflowAmounts, MonthlyCashflowAmounts, roundMonthlyCashflow } from "@/logics/thisMonthCashflow";
import { PropertyHolder } from "@/view_models/atoms";
import { MonthlyBalanceSheet } from "@/view_models/balancesheetSelectors";

const useStyles = makeStyles({
  card: {
    ...homeCardStyle,
    width: "100%",
    padding: layoutTokens.space[16],
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: layoutTokens.space[16],
    textAlign: "left",
    borderRadius: layoutTokens.radius.md,
  },
  heading: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: layoutTokens.space[8] },
  title: { ...textStyles["PC/Heading/H4"], color: semanticColors["text/primary"] },
  caption: { ...textStyles["PC/Caption"], color: semanticColors["text/secondary"] },
  total: { ...textStyles["Number/XL"], color: semanticColors["text/primary"], overflowWrap: "anywhere" },
  unit: { ...textStyles["PC/Caption"], marginLeft: layoutTokens.space[4] },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: layoutTokens.space[16],
    borderTop: `1px solid ${semanticColors["border/default"]}`,
    paddingTop: layoutTokens.space[16],
  },
  amount: {
    ...textStyles["Number/S"],
    color: semanticColors["text/primary"],
    marginTop: layoutTokens.space[4],
    overflowWrap: "anywhere",
  },
  arrow: { color: semanticColors["icon/primary"], fontSize: 20 },
  note: { ...textStyles["SP/Caption"], color: semanticColors["text/secondary"], padding: layoutTokens.space[8] },
  empty: {
    ...homeCardStyle,
    padding: `${layoutTokens.space[32]}px ${layoutTokens.space[24]}px`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: layoutTokens.space[16],
    textAlign: "center",
  },
  emptyDescription: {
    ...textStyles["PC/Body/Large"],
    color: semanticColors["text/secondary"],
    marginTop: layoutTokens.space[8],
  },
  register: { minWidth: 240, maxWidth: "100%" },
});

export type CashFlowSectionProps = {
  properties: PropertyHolder[];
  loans: {
    number_of_loan_contracts: number;
    total_amount: number;
    monthly_payment_amount: number;
    total_loan_remaining: number;
    properties: {
      id: string;
      name: string;
      remaining_loan_amount: number;
    }[];
  };
  propertyIdToThisMonthSheet: Map<number, MonthlyBalanceSheet>;
};

// 収支サマリーの計算。SPのカードとPC右レールのカードで共用する
export const buildCashFlowSummary = ({ properties, loans, propertyIdToThisMonthSheet }: CashFlowSectionProps) => {
  const sums = properties.reduce(
    (acc, property) => {
      const monthlyBalanceSheet = propertyIdToThisMonthSheet.get(property.id);
      const monthlyResult = monthlyBalanceSheet?.result;
      if (!monthlyResult) return acc;

      const amounts = buildMonthlyCashflowAmounts(property.property.property_category, monthlyResult);
      // 物件ごとの丸め済み金額は合算しない。内訳・合計とも共通ロジックの丸め前の値を使う。
      (Object.keys(acc) as Array<keyof MonthlyCashflowAmounts>).forEach((key) => {
        acc[key] += amounts[key];
      });
      return acc;
    },
    buildMonthlyCashflowAmounts(undefined, {})
  );

  // 収入項目の定義
  const income_items = [
    {
      key: "家賃収入",
      color: legacyColors.chartSeries.income[0],
      value: sums.rentIncome,
    },
    {
      key: "管理費収入",
      color: legacyColors.chartSeries.income[1],
      value: sums.managementFeeIncomeOnly,
    },
    {
      key: "その他収入",
      color: legacyColors.chartSeries.income[2],
      value: sums.otherIncome,
    },
  ];

  const expense_items = [
    {
      key: "ローン返済",
      color: legacyColors.chartSeries.expense[0],
      value: sums.loanRepayment,
    },
    {
      key: "建物管理費",
      color: legacyColors.chartSeries.expense[1],
      value: sums.buildingManagementCost,
    },
    {
      key: "修繕積立費",
      color: legacyColors.chartSeries.expense[2],
      value: sums.repairFundCost,
    },
    {
      key: "賃貸管理費",
      color: legacyColors.chartSeries.expense[3],
      value: sums.rentalManagementCost,
    },
    {
      key: "通信費",
      color: legacyColors.chartSeries.expense[4],
      value: sums.communicationCost,
    },

    {
      key: "その他経費",
      color: legacyColors.chartSeries.expense[5],
      value: sums.miscCost,
    },
    {
      key: "管理会社委託費（一棟）",
      color: legacyColors.chartSeries.expense[6],
      value: sums.buildingManagementCompanyCommission,
    },
    {
      key: "共用部水道光熱費（一棟）",
      color: legacyColors.chartSeries.expense[7],
      value: sums.buildingSharedUtilityExpenses,
    },
    {
      key: "その他経費（一棟）",
      color: legacyColors.chartSeries.expense[8],
      value: sums.buildingMiscCost,
    },
  ];

  // 収入グラフ用データ
  const incomeData = [
    {
      name: "収入",
      ...income_items.reduce(
        (acc, item) => ({
          ...acc,
          [item.key]: item.value,
        }),
        {}
      ),
    },
  ];

  // 支出グラフ用データ
  const expenseData = [
    {
      name: "支出",
      ...expense_items.reduce(
        (acc, item) => ({
          ...acc,
          [item.key]: item.value,
        }),
        {}
      ),
    },
  ];

  const { income, expense, totalCashFlow } = roundMonthlyCashflow(sums);

  // 資産合計
  const totalPriceOfProperty = properties.reduce((sum, property) => sum + property.property.total_price_of_property, 0);

  // ローン残高
  const totalLoanBalance = Math.round(loans.total_loan_remaining);

  // 棒グラフを相対表示するために最大値を計算
  const maxValue = Math.max(income, expense);

  return {
    income_items,
    expense_items,
    incomeData,
    expenseData,
    income,
    expense,
    totalCashFlow,
    totalPriceOfProperty,
    totalLoanBalance,
    maxValue,
  };
};

export const CashFlowSection = (props: CashFlowSectionProps) => {
  const classes = useStyles();
  const history = useHistory();
  const { income, expense, totalCashFlow, totalPriceOfProperty, totalLoanBalance } = buildCashFlowSummary(props);
  const rows = [
    ["収入合計", income],
    ["支出合計", expense],
    ["資産合計", totalPriceOfProperty],
    ["ローン残債", totalLoanBalance],
  ] as const;
  return (
    <Box>
      <ButtonBase id="home-tour-cashflow" className={classes.card} onClick={() => history.push("/balance")}>
        <Box className={classes.heading}>
          <Typography className={classes.title}>今月の合計収支</Typography>
          <ChevronRight className={classes.arrow} />
        </Box>
        <Box>
          <Typography className={classes.caption}>収入 − 支出</Typography>
          <Typography
            className={classes.total}
            style={{
              color:
                totalCashFlow > 0
                  ? semanticColors["status/success"]
                  : totalCashFlow < 0
                    ? semanticColors["status/error"]
                    : semanticColors["text/primary"],
            }}
            component="div"
          >
            {totalCashFlow > 0 ? "+" : ""}
            {totalCashFlow.toLocaleString()}
            <span className={classes.unit}>円</span>
          </Typography>
        </Box>
        <Box className={classes.grid}>
          {rows.map(([label, amount]) => (
            <Box key={label}>
              <Typography className={classes.caption}>{label}</Typography>
              <Typography className={classes.amount}>
                {amount.toLocaleString()}
                <span className={classes.unit}>円</span>
              </Typography>
            </Box>
          ))}
        </Box>
      </ButtonBase>
      <Box className={classes.note}>
        {props.properties.some((holder) => holder.property.property_type === PropertyType.NUMBER_3) && (
          <Typography className={classes.caption}>サンプル物件を含む収支です。</Typography>
        )}
        <CashflowRoundingNote />
      </Box>
    </Box>
  );
};

export const EmptyCashFlowSection: React.FC<{ onAddProperty: () => void }> = ({ onAddProperty }) => {
  const classes = useStyles();
  return (
    <Box id="home-tour-cashflow" className={classes.empty}>
      <Box>
        <Typography className={classes.title}>今月の合計収支</Typography>
        <Typography className={classes.emptyDescription}>
          物件を登録すると、毎月の収支・資産合計・ローンの残りがここに自動で出ます。
        </Typography>
      </Box>
      <CustomButton
        tone="outline"
        buttonSize="M"
        fullWidth={false}
        className={classes.register}
        onClick={onAddProperty}
      >
        物件を登録する
      </CustomButton>
    </Box>
  );
};
